import { Component, OnInit, OnDestroy, EventEmitter, Input, Output } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { EtiquetadoService } from "../../../../services/etiquetado.service";

@Component({
    selector: 'app-incidencia',
    templateUrl: './incidencia.component.html'
})
export class IncidenciaComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();
    public incidenciaForm: UntypedFormGroup;
    public incidenciaGestionada = false;
    public showError: boolean;
    private _multipleConsumos: boolean;

    public disabledEtqGuardadas: Subject<boolean> = new Subject();
    public disabledTiposInc: Subject<boolean> = new Subject();
    public disabledEtqAfectadas: Subject<boolean> = new Subject();

    @Input() incidenciaId;
    @Input() consumosList;
    @Input('multipleConsumos')

    set multipleConsumos(multiple: boolean) {
        this._multipleConsumos = multiple;
        if (multiple) {
            this.incidenciaForm.get('numEtiquetas').patchValue(this.etqAfectadas.find(x => x.value === 1));
            this.incidenciaForm.get('numEtiquetas').disable();
            this.disabledEtqAfectadas.next(true);
        } else {
            if (this.incidenciaForm) {
                this.incidenciaForm.get('numEtiquetas').enable();
                this.disabledEtqAfectadas.next(false);
            }
        }
    }

    get multipleConsumos(): boolean {
        return this._multipleConsumos;
    }

    @Output() mensaje = new EventEmitter<Message>();
    @Output() closingMessage = new EventEmitter<string>();
    @Output() close = new EventEmitter<boolean>();
    @Output() refresh = new EventEmitter<boolean>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _utilsService: UtilsService,
        private readonly _dataProvider: DataProvider
    ) { }

    ngOnInit() {
        this.incidenciaForm = this._fb.group({
            descripcion: ['', Validators.required],
            incidenciaTipo: ['', Validators.required],
            numEtiquetas: ['', Validators.required],
            numEtiquetasDesde: [''],
            numEtiquetasHasta: [''],
            numEtiquetasN: [''],
            etiquetasGuardadas: [false, Validators.required],
            nombrePersonaAlta: ['', Validators.required],
            emailPersonaAlta: ['', [Validators.required, Validators.pattern('^[A-z0-9._%+-]+@[A-z0-9.-]+\\.[A-z]{2,4}$')]],
            numEtiquetasEfectivas: [''],
            comentariosSoporte: [''],
            fechaGestion: ['']
        });

        this.incidenciaForm.get('incidenciaTipo').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(tipo => { if (tipo) { this.changeTipoIncidencia(); } });
        this.incidenciaForm.get('numEtiquetas').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(numEtq => { if (numEtq) { this.changeEtiquetasIncidencia(); } });

        if (this.rolePermission) {
            this.incidenciaForm.get('numEtiquetasEfectivas').setValidators(Validators.required);
            this.incidenciaForm.get('comentariosSoporte').setValidators(Validators.required);
        }

        if (this.incidenciaId) {
            this.getIncidencia();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get rolePermission() {
        return this._sessionContext.sessionUser.role === 'Administrador';
    }

    public get tiposIncidencia() {
        return this._dataProvider.tiposIncidencia;
    }

    public get etqAfectadas() {
        return this._dataProvider.etqAfectadas;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public changeTipoIncidencia() {
        if (this.incidenciaForm.get('incidenciaTipo').value.value === 7) {
            this.incidenciaForm.get('numEtiquetas').patchValue(this.etqAfectadas.find(x => x.value === 3));
            this.incidenciaForm.get('numEtiquetas').disable();
            this.disabledEtqAfectadas.next(true);
            this.incidenciaForm.get('numEtiquetasN').patchValue(0);
            this.incidenciaForm.get('numEtiquetasN').disable();
        } else if (this.multipleConsumos) {
            this.incidenciaForm.get('numEtiquetas').patchValue(this.etqAfectadas.find(x => x.value === 1));
            this.incidenciaForm.get('numEtiquetas').disable();
            this.disabledEtqAfectadas.next(true);
        } else if (!this.multipleConsumos) {
            this.incidenciaForm.get('numEtiquetas').enable();
            this.incidenciaForm.get('numEtiquetasN').enable();
            this.disabledEtqAfectadas.next(false);
        }
    }

    public changeEtiquetasIncidencia() {
        if (this.incidenciaForm.get('numEtiquetas').value.value === 2) {
            this.incidenciaForm.get('numEtiquetasN').reset();
            this.incidenciaForm.get('numEtiquetasN').clearValidators();
            this.incidenciaForm.get('numEtiquetasDesde').setValidators(Validators.required);
            this.incidenciaForm.get('numEtiquetasHasta').setValidators(Validators.required);
            this.incidenciaForm.get('numEtiquetasDesde').patchValue(this.consumosList[0].snc);
            this.incidenciaForm.get('numEtiquetasHasta').patchValue(this.consumosList[0].snc);
        } else if (this.incidenciaForm.get('numEtiquetas').value.value === 3) {
            this.incidenciaForm.get('numEtiquetasN').setValidators(Validators.required);
            this.incidenciaForm.get('numEtiquetasDesde').reset();
            this.incidenciaForm.get('numEtiquetasHasta').reset();
            this.incidenciaForm.get('numEtiquetasDesde').clearValidators();
            this.incidenciaForm.get('numEtiquetasHasta').clearValidators();
            if (!this.multipleConsumos) this.incidenciaForm.get('numEtiquetasN').enable();
        } else if (this.incidenciaForm.get('numEtiquetas').value.value === 1) {
            this.incidenciaForm.get('numEtiquetasN').reset();
            this.incidenciaForm.get('numEtiquetasDesde').reset();
            this.incidenciaForm.get('numEtiquetasHasta').reset();
            this.incidenciaForm.get('numEtiquetasN').clearValidators();
            this.incidenciaForm.get('numEtiquetasDesde').clearValidators();
            this.incidenciaForm.get('numEtiquetasHasta').clearValidators();
        }
    }

    public toClose() {
        this.close.emit(false);
    }

    private checkForm() {
        this.showError = this.incidenciaForm.invalid;
        if (this.showError) {
            this.mensaje.emit({ class: 'error', message: 'global.error.required' });
        }
        if (!this.consumosList || (this.consumosList && this.consumosList.length < 1)) {
            this.mensaje.emit({ class: 'error', message: 'incidencias.error.consumoRequired' });
        }
    }

    public save() {
        this.checkForm();
        if (this.incidenciaGestionada && !this.rolePermission) {
            this.toClose();
        } else if (this.incidenciaId && this.incidenciaForm.valid) {
            const incidencia = {
                incidenciaId: this.incidenciaId,
                des: this.incidenciaForm.get('descripcion').value,
                fecha: this._utilsService.parseGMTtoYYYYMMDD(new Date()),
                tipoIncidenciaRazonId: this.incidenciaForm.get('incidenciaTipo').value.value,
                tipoIncidenciaEtqAfectadaId: this.incidenciaForm.get('numEtiquetas').value.value,
                numeroEtiquetasSolicitadas: this.incidenciaForm.get('numEtiquetasN').value,
                etiquetasDisponibles: this.incidenciaForm.get('etiquetasGuardadas').value,
                rangoEtiquetasInicio:
                    this.incidenciaForm.get('numEtiquetasDesde').value ? [this.incidenciaForm.get('numEtiquetasDesde').value] : null,
                rangoEtiquetasFin:
                    this.incidenciaForm.get('numEtiquetasHasta').value ? [this.incidenciaForm.get('numEtiquetasHasta').value] : null,
                nombrePersonaAltaIncidencia: this.incidenciaForm.get('nombrePersonaAlta').value,
                correoPersonaAltaIncidencia: this.incidenciaForm.get('emailPersonaAlta').value,
                numeroEtiquetasEfectivas: this.incidenciaForm.get('numEtiquetasEfectivas').value,
                respuestaGST: this.incidenciaForm.get('comentariosSoporte').value
            };
            this._spinnerService.showSpinner();
            this._etiquetadoService.updateIncidencia(incidencia).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.close.emit(true);
                this.closingMessage.emit('global.success.itemUpdated');
                this.refresh.emit(true);
            }, (error) => {
                this.showError = true;
                switch (error.error.ApiErrorCode) {
                    case 6039:
                        this.mensaje.emit({ class: 'error', message: 'ApiErrors.numeroMayorEtiquetas'});
                        break;
                    case 6040:
                        this.mensaje.emit({ class: 'error', message: 'ApiErrors.consumoSuperaEtiquetasImpresas'});
                        break;
                    case 6041:
                        this.mensaje.emit({ class: 'error', message: 'ApiErrors.cambioConsumoNoPermitido'});
                        break;
                    case 6044:
                        this.mensaje.emit({ class: 'error', message: 'ApiErrors.consumoNoAccesible'});
                        break;
                    case 6251:
                        this.mensaje.emit({ class: 'error', message: 'incidencias.error.etiquetasEnUsoBorrar'});
                        break;
                    case 6252:
                        this.mensaje.emit({ class: 'error', message: 'incidencias.error.etiquetasEnUsoFirmados'});
                        break;
                    default:
                        this.mensaje.emit({ class: 'error', message: 'global.error.noResponse' });
                        break;
                }
            });
        } else if (!this.incidenciaId && this.incidenciaForm.valid) {
            const incidencia = {
                lsConsumoIds: this.consumosList.map(x => x.consumoId),
                des: this.incidenciaForm.get('descripcion').value,
                fecha: this._utilsService.parseGMTtoYYYYMMDD(new Date()),
                tipoIncidenciaRazonId: this.incidenciaForm.get('incidenciaTipo').value.value,
                tipoIncidenciaEtqAfectadaId: this.incidenciaForm.get('numEtiquetas').value.value,
                numeroEtiquetasSolicitadas: this.incidenciaForm.get('numEtiquetasN').value,
                etiquetasDisponibles: this.incidenciaForm.get('etiquetasGuardadas').value,
                rangoEtiquetasInicio:
                    this.incidenciaForm.get('numEtiquetasDesde').value ? [this.incidenciaForm.get('numEtiquetasDesde').value] : null,
                rangoEtiquetasFin:
                    this.incidenciaForm.get('numEtiquetasHasta').value ? [this.incidenciaForm.get('numEtiquetasHasta').value] : null,
                nombrePersonaAltaIncidencia: this.incidenciaForm.get('nombrePersonaAlta').value,
                correoPersonaAltaIncidencia: this.incidenciaForm.get('emailPersonaAlta').value,
                numeroEtiquetasEfectivas:
                    this.incidenciaForm.get('numEtiquetasEfectivas').value ? this.incidenciaForm.get('numEtiquetasEfectivas').value : '',
                respuestaGST: this.incidenciaForm.get('comentariosSoporte').value ? this.incidenciaForm.get('comentariosSoporte').value : ''
            };
            this._spinnerService.showSpinner();
            this._etiquetadoService.createIncidencia(incidencia).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.close.emit(true);
                this.closingMessage.emit('global.success.itemCreated');
                this.refresh.emit(true);
            }, (error) => {
                this.showError = true;
                switch (error.error.ApiErrorCode) {
                    case 6039:
                        this.mensaje.emit({ class: 'error', message: 'ApiErrors.numeroMayorEtiquetas'});
                        break;
                    case 6251:
                        this.mensaje.emit({ class: 'error', message: 'incidencias.error.etiquetasEnUsoBorrar'});
                        break;
                    case 6252:
                        this.mensaje.emit({ class: 'error', message: 'incidencias.error.etiquetasEnUsoFirmados'});
                        break;
                    default:
                        this.mensaje.emit({ class: 'error', message: 'global.error.noResponse' });
                        break;
                }
            });
        }
    }

    private getIncidencia() {
        this._spinnerService.showSpinner();
        this._etiquetadoService.getIncidencia(this.incidenciaId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.incidenciaGestionada = resp.gestionada;
            this.incidenciaForm.patchValue({
                descripcion: resp.des,
                incidenciaTipo: this.tiposIncidencia.find( x => x.value === resp.tipoIncidenciaRazonId),
                numEtiquetas: this.etqAfectadas.find( x => x.value === resp.tipoIncidenciaEtqAfectadaId),
                numEtiquetasN: resp.numeroEtiquetasSolicitadas,
                numEtiquetasDesde: resp.lsIncidenciaSeriadosSNC && resp.lsIncidenciaSeriadosSNC.length > 0 ?
                    resp.lsIncidenciaSeriadosSNC[0].item1 : null,
                numEtiquetasHasta: resp.lsIncidenciaSeriadosSNC && resp.lsIncidenciaSeriadosSNC.length > 0 ?
                    resp.lsIncidenciaSeriadosSNC[0].item2 : null,
                etiquetasGuardadas: resp.etiquetasDisponibles,
                nombrePersonaAlta: resp.nombrePersonaAltaIncidencia,
                emailPersonaAlta: resp.correoPersonaAltaIncidencia,
                numEtiquetasEfectivas: resp.numeroEtiquetasEfectivas,
                comentariosSoporte: resp.respuestaGST,
                fechaGestion: resp.fechaGestion ? this._utilsService.parseYYYYMMDDtoDDMMYYYY(resp.fechaGestion) : ''
            });
            if (resp.gestionada) {
                this.incidenciaForm.get('descripcion').disable();
                this.incidenciaForm.get('incidenciaTipo').disable();
                this.disabledTiposInc.next(true);
                this.incidenciaForm.get('numEtiquetas').disable();
                this.disabledEtqAfectadas.next(true);
                this.incidenciaForm.get('numEtiquetasN').disable();
                this.incidenciaForm.get('numEtiquetasDesde').disable();
                this.incidenciaForm.get('numEtiquetasHasta').disable();
                this.incidenciaForm.get('etiquetasGuardadas').disable();
                this.disabledEtqGuardadas.next(true);
                this.incidenciaForm.get('nombrePersonaAlta').disable();
                this.incidenciaForm.get('emailPersonaAlta').disable();
                if (!this.rolePermission) {
                    this.incidenciaForm.get('numEtiquetasEfectivas').disable();
                    this.incidenciaForm.get('comentariosSoporte').disable();
                }
            }
            this.incidenciaForm.updateValueAndValidity();
        });
    }

}
